import { toast } from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

import { useCheckReferralStatus } from "features/referrals/hooks/useCheckReferralStatus";
import {
  readRegeneratedLink,
  useResendReferral,
} from "features/referrals/hooks/useResendReferral";
import { getServerErrorMessage } from "utils/showMutationError";

// Actions for the currently-selected referral in the overview: mark refreshed,
// resend / regenerate the link, and manually check the Healthlink status.
export const useReferralActions = ({ selectedReferral, setSelectedReferral }) => {
  const queryClient = useQueryClient();
  const { mutate: resendReferral, isPending: isResending } = useResendReferral();
  const { mutateAsync: checkReferralStatus, isPending: isCheckingStatus } =
    useCheckReferralStatus();

  const handleRefresh = () => {
    setSelectedReferral((prev) => ({ ...prev, status: "SENT" }));
    toast.success("Status confirmed: Referral Sent");

    queryClient.invalidateQueries({ queryKey: ["referralsList"] });
  };

  const handleResendClick = () => {
    if (!selectedReferral?.id) return;

    resendReferral(
      { referralId: selectedReferral.id },
      {
        /*
         * What the resend did, not what it said — useResendReferral reports it, success
         * and failure both. This used to toast its own line over that one, twice for a
         * success and twice for a failure.
         */
        onSuccess: (data) => {
          const returnedLink = readRegeneratedLink(data);

          setSelectedReferral((prev) =>
            returnedLink
              ? {
                  ...prev,
                  status: "INTEGRATED_BROWSER",
                  xmlResponseData: returnedLink,
                }
              : { ...prev, status: "SENT" }
          );

          queryClient.invalidateQueries({ queryKey: ["referralsList"] });
        },
      }
    );
  };

  const handleCheckStatus = async () => {
    if (!selectedReferral?.id) return;

    try {
      const data = await checkReferralStatus(selectedReferral.id);

      if (data.confirmed) {
        setSelectedReferral((prev) => ({ ...prev, status: "SENT" }));
        toast.success("Referral submitted to Healthlink successfully!");
        queryClient.invalidateQueries({ queryKey: ["referralsList"] });
      } else {
        toast.error(
          "Healthlink has not confirmed receipt of this referral yet."
        );
      }
    } catch (error) {
      /*
       * Since KIN-1495 this is a 409 with a sentence when there is nothing to check —
       * the referral was never sent — and a 503 when Healthlink itself is unavailable.
       * Both are worth reading; "Failed to check referral status" was not.
       */
      console.error(error);
      toast.error(
        getServerErrorMessage(
          error,
          "Failed to check referral status. Please try again."
        )
      );
    }
  };

  return {
    handleRefresh,
    handleResendClick,
    handleCheckStatus,
    isResending,
    isCheckingStatus,
  };
};
